/* Shared score / confidence pill primitives for V3 workspace tabs.

   Consolidates three visual patterns that were duplicated across
   HeaderCard, DealScoreTab, ComparableSalesTab, and (upcoming) BP-008:

   • BAND_STYLES         — Deal Score band → {dot color, text class, label}
   • classifyConfidence  — 0-100 confidence → {label, dot, ring, text}
   • <ScoreBadge>        — vertical badge (icon+label / dot+value / band)
                           used in the workspace header
   • <ScorePill>         — compact horizontal pill (dot + value + label)
                           used inline in cards / rows
   • <ConfidencePill>    — 0-100 confidence tile (large value + classification)
                           used in Comparable Sales ARV card
*/
import React from "react";
import { Zap } from "lucide-react";

// ────────────────────────────────────────── Band map (BP-005)
export const BAND_STYLES = {
  elite:              { dot: "#10b981", text: "text-emerald-800", label: "ELITE" },
  strong_opportunity: { dot: "#10b981", text: "text-emerald-800", label: "STRONG" },
  good:               { dot: "#84cc16", text: "text-lime-700",    label: "GOOD" },
  review:             { dot: "#f59e0b", text: "text-amber-700",   label: "REVIEW" },
  high_risk:          { dot: "#ef4444", text: "text-red-700",     label: "HIGH RISK" },
};

export function bandStyle(band) {
  return BAND_STYLES[band] || { dot: "#94a3b8", text: "text-slate-500", label: "—" };
}

// ────────────────────────────────────────── Confidence classifier
export function classifyConfidence(score) {
  if (score === null || score === undefined) {
    return { label: "—", dot: "bg-slate-300", ring: "border-slate-200", text: "text-slate-400" };
  }
  if (score >= 85) return { label: "Excellent", dot: "bg-emerald-500", ring: "border-emerald-300", text: "text-emerald-700" };
  if (score >= 70) return { label: "Good",      dot: "bg-lime-500",    ring: "border-lime-300",    text: "text-lime-700" };
  if (score >= 50) return { label: "Fair",      dot: "bg-amber-500",   ring: "border-amber-300",   text: "text-amber-700" };
  return              { label: "Low",       dot: "bg-red-500",     ring: "border-red-300",     text: "text-red-700" };
}

// ────────────────────────────────────────── <ScoreBadge> — header vertical

export function ScoreBadge({
  score, band, label = "Deal Score", icon = <Zap size={13}/>,
  testid = "score-badge",
}) {
  const style = bandStyle(band);
  return (
    <div className="flex flex-col items-end" data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: style.dot }}
              aria-hidden="true"/>
        <span className="text-lg font-semibold text-slate-900 tabular-nums"
              data-testid={`${testid}-value`}>
          {score ?? "—"}
        </span>
      </div>
      <div className={`text-[9px] font-semibold uppercase tracking-wider ${style.text}`}
           data-testid={`${testid}-label`}>
        {score != null ? style.label : "not calculated"}
      </div>
    </div>
  );
}

// ────────────────────────────────────────── <ScorePill> — inline horizontal

export function ScorePill({
  score, band,
  dotHex,               // optional override (e.g., server-provided dot_hex)
  textHex,              // optional override (e.g., server-provided text_hex)
  label,                // optional override (else derived from band)
  suffix = "/100",
  size = "md",          // "sm" | "md" | "lg"
  testid = "score-pill",
}) {
  const style = bandStyle(band);
  const dot = dotHex || style.dot;
  const text = textHex ? undefined : style.text;
  const displayLabel = label ?? (score != null ? style.label : "—");
  const sizes = {
    sm: { dot: "w-2 h-2", value: "text-base", label: "text-[9px]" },
    md: { dot: "w-2.5 h-2.5", value: "text-xl", label: "text-[10px]" },
    lg: { dot: "w-4 h-4", value: "text-4xl", label: "text-sm" },
  }[size] || {};
  return (
    <div className="inline-flex items-center gap-2" data-testid={testid}
         data-score={score ?? ""} data-band={band || ""}>
      <span className={`inline-block ${sizes.dot} rounded-full flex-shrink-0`}
            style={{ backgroundColor: dot }} aria-hidden="true"/>
      <span className={`font-bold tabular-nums leading-none ${sizes.value} ${text || ""}`}
            style={textHex ? { color: textHex } : undefined}
            data-testid={`${testid}-value`}>
        {score ?? "—"}
        {suffix && <span className="text-[0.5em] text-slate-500 font-medium ml-1">{suffix}</span>}
      </span>
      <span className={`font-semibold uppercase tracking-wider ${sizes.label} ${text || ""}`}
            style={textHex ? { color: textHex } : undefined}
            data-testid={`${testid}-label`}>
        {displayLabel}
      </span>
    </div>
  );
}

// ────────────────────────────────────────── <ConfidencePill> — 0-100 tile

export function ConfidencePill({
  score, label = "Confidence", testid = "confidence-pill", awaitingText = "Awaiting calc",
}) {
  if (score === null || score === undefined) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-center"
           data-testid={testid}>
        <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-400 tabular-nums leading-tight">—</div>
        <div className="text-[10px] text-slate-500">{awaitingText}</div>
      </div>
    );
  }
  const c = classifyConfidence(score);
  return (
    <div className={`rounded-lg border ${c.ring} bg-white px-4 py-2 text-center min-w-[130px]`}
         data-testid={testid}
         data-confidence-classification={c.label.toLowerCase()}
         data-confidence-score={score}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${c.text} tabular-nums leading-tight`}
           data-testid={`${testid}-value`}>
        {score}<span className="text-lg">%</span>
      </div>
      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide">
        <span className={`w-2 h-2 rounded-full ${c.dot}`}/> {c.label}
      </div>
    </div>
  );
}

export default ScorePill;
